import React, { useState } from 'react';
import { destinations } from '@/data/destinations';
import { useAuth } from '@/contexts/AuthContext';
import { notifyForm } from '@/lib/notifyForm';
import { Check, ArrowRight, ArrowLeft, Send, Loader2 } from 'lucide-react';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  destination: string;
  experience: string;
  checkIn: string;
  checkOut: string;
  guests: string;
  budget: string;
  specialRequests: string;
}

const steps = [
  { id: 1, title: 'Your Details', subtitle: 'Tell us about yourself' },
  { id: 2, title: 'Your Journey', subtitle: 'Where in Albania would you like to go' },
  { id: 3, title: 'Preferences', subtitle: 'Help us personalize your Albanian experience' },
];

const PlanJourneyForm: React.FC = () => {
  const { user, profile, submitJourneyRequest } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const initialFormData: FormData = {
    firstName: profile?.first_name || '',
    lastName: profile?.last_name || '',
    email: user?.email || '',
    phone: profile?.phone || '',
    destination: '',
    experience: '',
    checkIn: '',
    checkOut: '',
    guests: '2',
    budget: profile?.preferred_budget || '',
    specialRequests: '',
  };

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [submitted, setSubmitted] = useState(false);

  // Sync profile data when user logs in
  React.useEffect(() => {
    if (profile) {
      setFormData((prev) => ({
        ...prev,
        firstName: prev.firstName || profile.first_name || '',
        lastName: prev.lastName || profile.last_name || '',
        email: prev.email || user?.email || '',
        phone: prev.phone || profile.phone || '',
        budget: prev.budget || profile.preferred_budget || '',
      }));
    }
  }, [profile, user]);

  const updateField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<FormData> = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'Required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Required';
      if (!formData.email.trim()) newErrors.email = 'Required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    }

    if (step === 2) {
      if (!formData.destination) newErrors.destination = 'Please select a destination';
      if (!formData.checkIn) newErrors.checkIn = 'Required';
      if (!formData.checkOut) newErrors.checkOut = 'Required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 3));
    }
  };

  const handlePrev = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setSubmitting(true);
    const { error } = await submitJourneyRequest({
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      destination: formData.destination,
      experience: formData.experience,
      check_in: formData.checkIn,
      check_out: formData.checkOut,
      guests: formData.guests,
      budget: formData.budget,
      special_requests: formData.specialRequests,
    });

    // Add to CRM + email journey@zeratravel.com (non-blocking)
    notifyForm({
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      source: 'plan-journey',
      subject: `Zera Travel — New Journey Request from ${formData.firstName} ${formData.lastName}`,
      details: {
        destination: formData.destination,
        experience: formData.experience,
        check_in: formData.checkIn,
        check_out: formData.checkOut,
        guests: formData.guests,
        budget: formData.budget,
        special_requests: formData.specialRequests,
      },
    });

    setSubmitting(false);

    if (!error) {
      setSubmitted(true);
    }
  };


  const inputClass = (field: keyof FormData) =>
    `w-full bg-transparent border-b ${
      errors[field] ? 'border-red-400' : 'border-[#C9A961]/30'
    } py-3 font-sans text-sm text-[#2C2C2C] placeholder:text-[#999] focus:outline-none focus:border-[#C9A961] transition-colors`;

  const selectClass = (field: keyof FormData) =>
    `w-full bg-transparent border-b ${
      errors[field] ? 'border-red-400' : 'border-[#C9A961]/30'
    } py-3 font-sans text-sm text-[#2C2C2C] focus:outline-none focus:border-[#C9A961] transition-colors appearance-none cursor-pointer`;

  if (submitted) {
    return (
      <section id="plan-journey" className="py-24 lg:py-32 bg-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-20 h-20 rounded-full bg-[#C9A961]/10 flex items-center justify-center mx-auto mb-8">
            <Check className="w-10 h-10 text-[#C9A961]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#1A3B5C] mb-4">
            Faleminderit, {formData.firstName}!
          </h2>
          <p className="font-sans text-sm text-[#777] leading-relaxed mb-3">
            Your Albanian journey request has been received. Our dedicated concierge team will craft a personalized itinerary
            and reach out to you within 24 hours at <span className="text-[#1A3B5C]">{formData.email}</span>.
          </p>
          {user && (
            <p className="font-sans text-xs text-[#999] mb-8">
              You can track the status of this request in your profile.
            </p>
          )}
          <button
            onClick={() => {
              setSubmitted(false);
              setCurrentStep(1);
              setFormData({
                ...initialFormData,
                firstName: profile?.first_name || '',
                lastName: profile?.last_name || '',
                email: user?.email || '',
                phone: profile?.phone || '',
                budget: profile?.preferred_budget || '',
              });
            }}
            className="font-sans text-[10px] tracking-[0.2em] uppercase px-8 py-3.5 border border-[#1A3B5C] text-[#1A3B5C] hover:bg-[#1A3B5C] hover:text-white transition-all"
          >
            Plan Another Journey
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="plan-journey" className="py-24 lg:py-32 bg-white">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961] block mb-4">
            Begin Your Albanian Story
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A3B5C] font-light mb-6 gold-underline">
            Plan Your Journey
          </h2>
          <p className="font-sans text-sm text-[#777] max-w-lg mx-auto leading-relaxed mt-10">
            Share your vision and let our Albanian travel experts craft an experience beyond your imagination.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          {/* Step Indicators */}
          <div className="flex items-center justify-center mb-14">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-sans transition-all duration-300 ${
                      currentStep >= step.id
                        ? 'bg-[#1A3B5C] text-white'
                        : 'bg-[#F5F1E8] text-[#999]'
                    }`}
                  >
                    {currentStep > step.id ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      step.id
                    )}
                  </div>
                  <span className="font-sans text-[10px] tracking-wider uppercase mt-2 text-[#999] hidden sm:block">
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`w-16 sm:w-24 h-[1px] mx-3 transition-colors duration-300 ${
                      currentStep > step.id ? 'bg-[#C9A961]' : 'bg-[#E8DFD0]'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step Content */}
          <div className="bg-[#FAF8F3] p-8 md:p-12">
            <h3 className="font-serif text-2xl text-[#1A3B5C] mb-1">
              {steps[currentStep - 1].title}
            </h3>
            <p className="font-sans text-xs text-[#999] mb-8">
              {steps[currentStep - 1].subtitle}
            </p>

            {/* Step 1: Personal Details */}
            {currentStep === 1 && (
              <div className="space-y-6">
                {user && (
                  <div className="bg-white/60 border border-[#C9A961]/20 p-4 mb-2">
                    <p className="font-sans text-xs text-[#C9A961]">
                      Signed in as {user.email}. Your details have been pre-filled.

                    </p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => updateField('firstName', e.target.value)}
                      className={inputClass('firstName')}
                      placeholder="Elena"
                    />
                    {errors.firstName && (
                      <span className="font-sans text-[10px] text-red-400 mt-1 block">{errors.firstName}</span>
                    )}
                  </div>
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => updateField('lastName', e.target.value)}
                      className={inputClass('lastName')}
                      placeholder="Marchetti"
                    />
                    {errors.lastName && (
                      <span className="font-sans text-[10px] text-red-400 mt-1 block">{errors.lastName}</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    className={inputClass('email')}
                    placeholder="elena@example.com"
                  />
                  {errors.email && (
                    <span className="font-sans text-[10px] text-red-400 mt-1 block">{errors.email}</span>
                  )}
                </div>
                <div>
                  <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    className={inputClass('phone')}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Journey Details */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1">
                    Preferred Destination *
                  </label>
                  <select
                    value={formData.destination}
                    onChange={(e) => updateField('destination', e.target.value)}
                    className={selectClass('destination')}
                  >
                    <option value="">Select a destination</option>
                    {destinations.map((d) => (
                      <option key={d.id} value={d.name}>
                        {d.name} - {d.location}

                      </option>
                    ))}
                  </select>
                  {errors.destination && (
                    <span className="font-sans text-[10px] text-red-400 mt-1 block">{errors.destination}</span>
                  )}
                </div>
                <div>
                  <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1">
                    Experience Type
                  </label>
                  <select
                    value={formData.experience}
                    onChange={(e) => updateField('experience', e.target.value)}
                    className={selectClass('experience')}
                  >
                    <option value="">Select an experience</option>
                    <option value="albanian-riviera">Albanian Riviera</option>
                    <option value="ottoman-heritage">Ottoman Heritage Tour</option>
                    <option value="alps-trek">Albanian Alps Trek</option>
                    <option value="culinary-albania">Culinary Albania</option>
                    <option value="archaeological-odyssey">Archaeological Odyssey</option>
                    <option value="boutique-retreat">Boutique Retreat</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1">
                      Check-in Date *
                    </label>
                    <input
                      type="date"
                      value={formData.checkIn}
                      onChange={(e) => updateField('checkIn', e.target.value)}
                      className={inputClass('checkIn')}
                    />
                    {errors.checkIn && (
                      <span className="font-sans text-[10px] text-red-400 mt-1 block">{errors.checkIn}</span>
                    )}
                  </div>
                  <div>
                    <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1">
                      Check-out Date *
                    </label>
                    <input
                      type="date"
                      value={formData.checkOut}
                      onChange={(e) => updateField('checkOut', e.target.value)}
                      className={inputClass('checkOut')}
                    />
                    {errors.checkOut && (
                      <span className="font-sans text-[10px] text-red-400 mt-1 block">{errors.checkOut}</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1">
                    Number of Guests
                  </label>
                  <select
                    value={formData.guests}
                    onChange={(e) => updateField('guests', e.target.value)}
                    className={selectClass('guests')}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                      <option key={n} value={n}>
                        {n} {n === 1 ? 'Guest' : 'Guests'}
                      </option>
                    ))}
                    <option value="9+">9+ Guests</option>
                  </select>
                </div>
              </div>
            )}

            {/* Step 3: Preferences */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1">
                    Budget Range (per person)
                  </label>
                  <select
                    value={formData.budget}
                    onChange={(e) => updateField('budget', e.target.value)}
                    className={selectClass('budget')}
                  >
                    <option value="">Prefer not to say</option>
                    <option value="500-1000">€500 - €1,000</option>
                    <option value="1000-2500">€1,000 - €2,500</option>
                    <option value="2500-5000">€2,500 - €5,000</option>
                    <option value="5000-10000">€5,000 - €10,000</option>
                    <option value="10000+">€10,000+</option>
                  </select>
                </div>
                <div>
                  <label className="font-sans text-[10px] tracking-[0.15em] uppercase text-[#999] block mb-1">
                    Special Requests & Preferences
                  </label>
                  <textarea
                    value={formData.specialRequests}
                    onChange={(e) => updateField('specialRequests', e.target.value)}
                    className="w-full bg-transparent border border-[#C9A961]/30 p-4 font-sans text-sm text-[#2C2C2C] placeholder:text-[#999] focus:outline-none focus:border-[#C9A961] transition-colors min-h-[120px] resize-none"
                    placeholder="Dietary requirements, celebration details, accessibility needs, preferred activities, interest in specific Albanian regions..."
                  />
                </div>
                <div className="bg-white p-6 border border-[#E8DFD0]">
                  <h4 className="font-serif text-lg text-[#1A3B5C] mb-3">Journey Summary</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-sans text-[10px] uppercase text-[#999] block">Guest</span>
                      <span className="font-sans text-[#2C2C2C]">{formData.firstName} {formData.lastName}</span>
                    </div>
                    <div>
                      <span className="font-sans text-[10px] uppercase text-[#999] block">Destination</span>
                      <span className="font-sans text-[#2C2C2C]">{formData.destination || 'Not selected'}</span>
                    </div>
                    <div>
                      <span className="font-sans text-[10px] uppercase text-[#999] block">Dates</span>
                      <span className="font-sans text-[#2C2C2C]">
                        {formData.checkIn && formData.checkOut
                          ? `${formData.checkIn} to ${formData.checkOut}`

                          : 'Not selected'}
                      </span>
                    </div>
                    <div>
                      <span className="font-sans text-[10px] uppercase text-[#999] block">Guests</span>
                      <span className="font-sans text-[#2C2C2C]">{formData.guests}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-10 pt-8 border-t border-[#E8DFD0]">
              {currentStep > 1 ? (
                <button
                  onClick={handlePrev}
                  className="font-sans text-[10px] tracking-[0.2em] uppercase flex items-center gap-2 text-[#999] hover:text-[#1A3B5C] transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> Previous
                </button>
              ) : (
                <div />
              )}

              {currentStep < 3 ? (
                <button
                  onClick={handleNext}
                  className="font-sans text-[10px] tracking-[0.2em] uppercase px-8 py-3.5 bg-[#1A3B5C] text-white hover:bg-[#2a5580] transition-colors flex items-center gap-2"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="font-sans text-[10px] tracking-[0.2em] uppercase px-8 py-3.5 bg-[#C9A961] text-white hover:bg-[#b8944d] transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
                    </>
                  ) : (
                    <>
                      Submit Request <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlanJourneyForm;
